import React, { useState, useCallback, useMemo } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip,
  Badge,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { sidebarConfig } from "../config/sidebarConfig";
import { getSidebarItemAccess } from "../domain/navigation/sidebarAccess";
import { getLibraryNavPath } from "../utils/libraryNav";
import { usePendingJoinRequestCount } from "../hooks/usePendingJoinRequestCount";
import { isCoursePath } from "../domain/help/course";

interface SidebarProps {
  minimized: boolean;
  onToggleMinimized: (min: boolean) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const createSidebarStyles = (
  theme: any,
  isMobile: boolean,
  minimized: boolean
) => ({
  drawer: {
    width: isMobile
      ? "100vw"
      : minimized
      ? sidebarConfig.minimizedWidth
      : sidebarConfig.expandedWidth,
    flexShrink: 0,
    zIndex: isMobile ? 1400 : "auto",
    transition: isMobile
      ? "none"
      : "width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    "& .MuiDrawer-paper": {
      width: isMobile
        ? "100vw"
        : minimized
        ? sidebarConfig.minimizedWidth
        : sidebarConfig.expandedWidth,
      boxSizing: "border-box" as const,
      borderRight: "none",
      // Ensure no rounded corners or global Paper border
      borderRadius: 0,
      border: "none",
      bgcolor: theme.palette.primary.main,
      boxShadow: isMobile ? "none" : `2px 0 8px ${theme.palette.shadows.dark}`,
      zIndex: isMobile ? 1400 : "auto",
      transition: isMobile
        ? "none"
        : "width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      overflow: "hidden",
      maxHeight: "100vh", // Ensure drawer doesn't exceed viewport height
      display: "flex",
      flexDirection: "column" as const,
    },
  },
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    bgcolor: theme.palette.primary.main,
    overflow: "hidden", // Always hidden to allow list to scroll
  },
  toolbar: {
    minHeight: 64,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 3, // 24px
    borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
    bgcolor: theme.palette.primary.main,
  },
  title: {
    color: "common.white",
    textAlign: "left" as const,
    flex: 1,
    display: minimized && !isMobile ? "none" : "block",
    fontSize: 20,
    fontWeight: 700,
    transition: "opacity 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    opacity: minimized && !isMobile ? 0 : 1,
  },
  closeButton: {
    color: "common.white",
    ml: "auto",
    "&:hover": {
      bgcolor: alpha(theme.palette.common.white, 0.1),
    },
  },
  list: {
    flex: 1,
    bgcolor: theme.palette.primary.main,
    p: minimized && !isMobile ? 1 : 2,
    ...(minimized && !isMobile
      ? {
          display: "flex",
          flexDirection: "column" as const,
          alignItems: "center",
        }
      : {}),
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0, // Important for flex scrolling
    // Custom scrollbar styling
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: alpha(theme.palette.common.white, 0.05),
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: alpha(theme.palette.common.white, 0.2),
      borderRadius: "3px",
      "&:hover": {
        background: alpha(theme.palette.common.white, 0.3),
      },
    },
    // Firefox scrollbar styling
    scrollbarWidth: "thin",
    scrollbarColor: `${alpha(theme.palette.common.white, 0.2)} ${alpha(
      theme.palette.common.white,
      0.05
    )}`,
    "& .MuiListItemButton-root": {
      mb: 0.5, // 4px spacing between items
    },
  },
  mobileLogoContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    mb: 2,
    px: 2,
    cursor: "pointer",
    userSelect: "none" as const,
    transition: "background 0.2s",
    "&:active": { opacity: 0.7 },
  },
  mobileLogo: {
    width: 90,
    height: 90,
    borderRadius: "50%",
    border: 2,
    borderColor: alpha(theme.palette.common.white, 0.3),
    objectFit: "cover" as const,
  },
  mobileSchoolName: {
    color: "common.white",
    fontWeight: 500,
    fontSize: "1rem",
    mt: 1.5,
    textAlign: "center" as const,
    wordBreak: "break-word" as const,
    maxWidth: "100%",
    mb: 2,
  },
  divider: {
    bgcolor: "common.white",
    mx: 2,
    mb: 2,
    height: 1,
    width: "calc(100% - 32px)",
  },
  listItemButton: {
    borderRadius: minimized && !isMobile ? "10px" : "8px",
    mx: isMobile ? 1 : minimized ? "auto" : 0,
    mb: 0.5,
    justifyContent: isMobile ? "center" : minimized ? "center" : "flex-start",
    px: minimized && !isMobile ? 0 : 2,
    py: minimized && !isMobile ? 0 : 1.5,
    gap: minimized && !isMobile ? 0 : 1.5,
    color: "common.white",
    transition: "background-color 0.2s ease-in-out",
    ...(minimized && !isMobile
      ? {
          width: 40,
          height: 40,
          minHeight: 40,
          minWidth: 40,
          maxWidth: 40,
          flexShrink: 0,
        }
      : {}),
    "&:hover": {
      bgcolor: alpha(theme.palette.common.white, 0.1),
    },
    "&.Mui-selected": {
      bgcolor: alpha(theme.palette.common.white, 0.2),
      justifyContent: isMobile ? "center" : minimized ? "center" : "flex-start",
      "&:hover": {
        bgcolor: alpha(theme.palette.common.white, 0.2),
      },
    },
  },
  listItemIcon: {
    minWidth: minimized && !isMobile ? 0 : 20,
    mr: minimized ? 0 : 1.5, // 12px gap before text
    justifyContent: "center",
    color: "common.white",
    width: 20,
    height: 20,
    "& .MuiSvgIcon-root": { fontSize: 20 },
  },
  listItemText: {
    transition:
      "opacity 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    opacity: minimized && !isMobile ? 0 : 1,
    width: minimized && !isMobile ? 0 : "auto",
    overflow: "hidden",
    whiteSpace: "nowrap" as const,
    ml: minimized && !isMobile ? 0 : 0,
    "& .MuiListItemText-primary": {
      color: "common.white",
      fontWeight: 500, // medium
      fontSize: theme.typography.body2.fontSize,
    },
  },
  toggleButton: {
    mx: minimized ? "auto" : 2,
    mb: 2,
    bgcolor: alpha(theme.palette.common.white, 0.1),
    color: "common.white",
    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    width: minimized ? 40 : "85%",
    height: minimized ? 40 : 36,
    borderRadius: minimized ? "10px" : 2,
    flexShrink: 0,
    transition: "all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    "&:hover": {
      bgcolor: alpha(theme.palette.common.white, 0.2),
    },
  },
});

const Sidebar: React.FC<SidebarProps> = ({
  minimized,
  onToggleMinimized,
  mobileOpen: mobileOpenProp,
  onMobileClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpenState, setMobileOpenState] = useState(false);
  const mobileOpen =
    typeof mobileOpenProp === "boolean" ? mobileOpenProp : mobileOpenState;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const pendingJoinRequestCount = usePendingJoinRequestCount();

  const handleMobileClose = useCallback(() => {
    if (onMobileClose) onMobileClose();
    else setMobileOpenState(false);
  }, [onMobileClose]);

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
      if (isMobile) handleMobileClose();
    },
    [navigate, isMobile, handleMobileClose]
  );

  const handleMobileLogoClick = useCallback(() => {
    navigate("/profile");
    handleMobileClose();
  }, [navigate, handleMobileClose]);

  const handleToggleMinimized = useCallback(() => {
    onToggleMinimized(!minimized);
  }, [onToggleMinimized, minimized]);

  const styles = useMemo(
    () => createSidebarStyles(theme, isMobile, minimized),
    [theme, isMobile, minimized]
  );

  const navigationItems = useMemo(
    () =>
      sidebarConfig.navItems
        .map((item, idx) => {
          const IconComponent = item.icon;
          const navPath = item.libraryHub ? getLibraryNavPath(user) : item.path;

          const access = getSidebarItemAccess(item, user);
          const pathname = location.pathname;
          const isSelected =
            item.libraryHub
              ? pathname === "/library" ||
                pathname.startsWith("/create/worksheets") ||
                pathname === "/resources" ||
                pathname.startsWith("/resources/")
              : item.key === "helpCenter"
                ? pathname === item.path || isCoursePath(pathname)
                : pathname === item.path;

          return {
            ...item,
            navPath,
            iconElement: item.iconSrc ? (
              <Box
                component="img"
                src={item.iconSrc}
                alt={`${item.label} icon`}
                sx={{ width: 20, height: 20, objectFit: "contain" }}
              />
            ) : (
              <IconComponent key={item.label} sx={{ fontSize: 20 }} />
            ),
            isDisabled: access === "disabled",
            isHidden: access === "hidden",
            isSelected,
          };
        })
        .filter((item) => !item.isHidden), // Filter out hidden items
    [location.pathname, user]
  );

  const drawerContent = (
    <Box sx={styles.container}>
      <Toolbar sx={styles.toolbar}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={styles.title}
          component="h1"
        >
          Noticeboard
        </Typography>
        {isMobile && (
          <IconButton
            size="small"
            onClick={handleMobileClose}
            sx={styles.closeButton}
            aria-label="Close mobile menu"
          >
            <span style={{ fontSize: 24, fontWeight: 700 }}>&times;</span>
          </IconButton>
        )}
      </Toolbar>
      <List sx={styles.list}>
        {isMobile && (
          <Box
            sx={styles.mobileLogoContainer}
            onClick={handleMobileLogoClick}
            role="button"
            tabIndex={0}
            aria-label="Go to account page"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleMobileLogoClick();
              }
            }}
          >
            <Box
              component="img"
              src={user?.schoolLogo || "/placeholder.jpg"}
              alt="School Logo"
              sx={styles.mobileLogo}
              key={user?.schoolLogo} // Force re-render when logo changes
            />
            {user?.schoolName && (
              <Typography variant="subtitle1" sx={styles.mobileSchoolName}>
                {user.schoolName}
              </Typography>
            )}
            {user?.schoolName && <Box sx={styles.divider} />}
          </Box>
        )}
        {navigationItems.map((item) => {
          const showJoinRequestDot =
            item.path === "/user-management" && pendingJoinRequestCount > 0;
          const navButton = (
            <ListItemButton
              selected={item.isSelected}
              onClick={() => !item.isDisabled && handleNav(item.navPath)}
              disabled={item.isDisabled}
              sx={{
                ...styles.listItemButton,
                opacity: item.isDisabled ? 0.5 : 1,
                cursor: item.isDisabled ? "not-allowed" : "pointer",
              }}
              aria-label={item.label}
              aria-current={item.isSelected ? "page" : undefined}
              data-testid={`sidebar-nav-${item.path.replace(/\//g, '-').replace(/^-/, '') || 'home'}`}
            >
              {!isMobile && (
                <ListItemIcon
                  sx={{
                    ...styles.listItemIcon,
                    opacity: item.isDisabled ? 0.5 : 1,
                  }}
                >
                  {showJoinRequestDot ? (
                    <Badge
                      color="error"
                      variant="dot"
                      overlap="circular"
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      {item.iconElement}
                    </Badge>
                  ) : (
                    item.iconElement
                  )}
                </ListItemIcon>
              )}
              {(!minimized || isMobile) && (
                <Box sx={styles.listItemText}>
                  <ListItemText
                    primary={
                      showJoinRequestDot ? (
                        <Badge
                          color="error"
                          variant="dot"
                          invisible={false}
                          sx={{
                            "& .MuiBadge-badge": {
                              top: 6,
                              right: -8,
                            },
                          }}
                        >
                          <Box component="span">{item.label}</Box>
                        </Badge>
                      ) : (
                        item.label
                      )
                    }
                  />
                </Box>
              )}
            </ListItemButton>
          );

          return (
            <Tooltip
              key={item.label}
              title={
                minimized
                  ? item.label
                  : item.isDisabled
                  ? "School approval required"
                  : item.label
              }
              placement="right"
            >
              {minimized && !isMobile ? (
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {navButton}
                </Box>
              ) : (
                navButton
              )}
            </Tooltip>
          );
        })}
      </List>

      {/* Toggle button at bottom of sidebar */}
      {!isMobile && (
        <IconButton
          size="medium"
          onClick={handleToggleMinimized}
          aria-label={minimized ? "Expand sidebar" : "Collapse sidebar"}
          sx={styles.toggleButton}
          data-testid="sidebar-toggle-button"
        >
          {minimized ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          {!minimized && (
            <Typography
              variant="body2"
              sx={{ ml: 1, color: "common.white", fontSize: "0.75rem" }}
            >
              {minimized ? "Expand" : "Collapse"}
            </Typography>
          )}
        </IconButton>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: "100vh" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={styles.drawer}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
